import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Buildings, ChartBar, Warning, Shield, Target, Users,
  DownloadSimple, X, Calendar, FileText, Plus, CaretLeft,
  ChartPie, Gauge, CheckCircle, Info, ArrowRight
} from '@phosphor-icons/react';
import { API, useAuth } from '@/App';
import Sidebar from '@/components/Sidebar';

// Maturity score to color mapping
const getMaturityColor = (score) => {
  if (score >= 3.5) return { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30' };
  if (score >= 2.5) return { bg: 'bg-lime-500/20', text: 'text-lime-400', border: 'border-lime-500/30' };
  if (score >= 1.5) return { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30' };
  if (score >= 0.5) return { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30' };
  return { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' };
};

// Status badge for risk appetite
const StatusBadge = ({ status }) => {
  const styles = {
    'Within Appetite': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    'Near Target': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    'Below Target': 'bg-red-500/20 text-red-400 border-red-500/30'
  };
  return (
    <span className={`px-2 py-1 rounded-sm text-xs font-mono border ${styles[status] || styles['Below Target']}`}>
      {status}
    </span>
  );
};

// Risk level badge
const RiskBadge = ({ level }) => {
  const styles = {
    Critical: 'bg-red-500/20 text-red-400 border-red-500/30',
    High: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    Medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    Low: 'bg-green-500/20 text-green-400 border-green-500/30'
  };
  return (
    <span className={`px-2 py-1 rounded-sm text-xs font-mono border ${styles[level] || styles.Medium}`}>
      {level}
    </span>
  );
};

// Circular progress component
const MaturityRing = ({ score, maxScore = 4, size = 160 }) => {
  const percentage = (score / maxScore) * 100;
  const strokeWidth = 12;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  const colors = getMaturityColor(score);
  const strokeColor = score >= 3.5 ? '#34d399' : score >= 2.5 ? '#a3e635' : score >= 1.5 ? '#facc15' : score >= 0.5 ? '#fb923c' : '#ef4444';

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#1a1a1a" strokeWidth={strokeWidth} />
        <circle
          cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke={strokeColor} strokeWidth={strokeWidth}
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-4xl font-mono font-bold text-text-primary">{score.toFixed(1)}</span>
        <span className="text-xs text-text-tertiary">of {maxScore}</span>
      </div>
    </div>
  );
};

// Create Report Modal
const CreateReportModal = ({ isOpen, onClose, onSuccess, groupId }) => {
  const [formData, setFormData] = useState({
    reporting_period: '',
    reporting_cycle: 'Monthly',
    completed_by: '',
    completed_by_role: '',
    key_priorities: '',
    executive_summary: ''
  });
  const [engagements, setEngagements] = useState([]);
  const [newEngagement, setNewEngagement] = useState({ title: '', date: '', regulator: '' });
  const [loading, setLoading] = useState(false);

  const handleAddEngagement = () => {
    if (newEngagement.title && newEngagement.date) {
      setEngagements([...engagements, newEngagement]);
      setNewEngagement({ title: '', date: '', regulator: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.reporting_period || !formData.completed_by) {
      toast.error('Please fill reporting period and completed by');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        key_priorities: formData.key_priorities.split('\n').filter(p => p.trim()),
        upcoming_engagements: engagements
      };

      await axios.post(`${API}/groups/${groupId}/dashboard/report`, payload);
      toast.success('Report created successfully');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create report');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-background-surface border border-border rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-heading font-semibold text-text-primary">Create Dashboard Report</h2>
          <button onClick={onClose} className="text-text-tertiary hover:text-text-primary">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-text-secondary mb-2">Reporting Period *</label>
              <input
                type="text"
                value={formData.reporting_period}
                onChange={(e) => setFormData({ ...formData, reporting_period: e.target.value })}
                placeholder="e.g., Q1 2026, March 2026"
                className="w-full px-4 py-3 bg-background border border-border rounded-sm text-text-primary focus:border-gold focus:outline-none"
                data-testid="report-period-input"
              />
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-2">Reporting Cycle</label>
              <select
                value={formData.reporting_cycle}
                onChange={(e) => setFormData({ ...formData, reporting_cycle: e.target.value })}
                className="w-full px-4 py-3 bg-background border border-border rounded-sm text-text-primary focus:border-gold focus:outline-none"
              >
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Annually">Annually</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-text-secondary mb-2">Completed By *</label>
              <input
                type="text"
                value={formData.completed_by}
                onChange={(e) => setFormData({ ...formData, completed_by: e.target.value })}
                placeholder="Name of person completing"
                className="w-full px-4 py-3 bg-background border border-border rounded-sm text-text-primary focus:border-gold focus:outline-none"
                data-testid="completed-by-input"
              />
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-2">Role</label>
              <input
                type="text"
                value={formData.completed_by_role}
                onChange={(e) => setFormData({ ...formData, completed_by_role: e.target.value })}
                placeholder="e.g., Group Compliance Officer"
                className="w-full px-4 py-3 bg-background border border-border rounded-sm text-text-primary focus:border-gold focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-text-secondary mb-2">Executive Summary</label>
            <textarea
              value={formData.executive_summary}
              onChange={(e) => setFormData({ ...formData, executive_summary: e.target.value })}
              placeholder="Brief summary of compliance posture..."
              rows={3}
              className="w-full px-4 py-3 bg-background border border-border rounded-sm text-text-primary focus:border-gold focus:outline-none resize-none"
            />
          </div>

          <div>
            <label className="block text-sm text-text-secondary mb-2">Key Priorities (one per line)</label>
            <textarea
              value={formData.key_priorities}
              onChange={(e) => setFormData({ ...formData, key_priorities: e.target.value })}
              placeholder="Priority 1&#10;Priority 2&#10;Priority 3"
              rows={3}
              className="w-full px-4 py-3 bg-background border border-border rounded-sm text-text-primary focus:border-gold focus:outline-none resize-none font-mono text-sm"
            />
          </div>

          <div>
            <label className="block text-sm text-text-secondary mb-2">Upcoming Engagements</label>
            <div className="space-y-2 mb-3">
              {engagements.map((eng, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 bg-background rounded-sm">
                  <span className="text-sm text-text-primary flex-1">{eng.title}</span>
                  <span className="text-xs text-text-tertiary">{eng.date}</span>
                  <span className="text-xs text-gold">{eng.regulator}</span>
                  <button
                    type="button"
                    onClick={() => setEngagements(engagements.filter((_, i) => i !== idx))}
                    className="text-red-400 hover:text-red-300"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newEngagement.title}
                onChange={(e) => setNewEngagement({ ...newEngagement, title: e.target.value })}
                placeholder="Engagement title"
                className="flex-1 px-3 py-2 bg-background border border-border rounded-sm text-text-primary text-sm focus:border-gold focus:outline-none"
              />
              <input
                type="date"
                value={newEngagement.date}
                onChange={(e) => setNewEngagement({ ...newEngagement, date: e.target.value })}
                className="px-3 py-2 bg-background border border-border rounded-sm text-text-primary text-sm focus:border-gold focus:outline-none"
              />
              <input
                type="text"
                value={newEngagement.regulator}
                onChange={(e) => setNewEngagement({ ...newEngagement, regulator: e.target.value })}
                placeholder="Regulator"
                className="w-24 px-3 py-2 bg-background border border-border rounded-sm text-text-primary text-sm focus:border-gold focus:outline-none"
              />
              <button
                type="button"
                onClick={handleAddEngagement}
                className="px-3 py-2 bg-gold/20 text-gold rounded-sm hover:bg-gold/30"
              >
                <Plus size={20} />
              </button>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 border border-border rounded-sm text-text-secondary hover:bg-background-hover transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 bg-gold text-gold-foreground rounded-sm font-medium hover:bg-gold/90 transition-colors disabled:opacity-50"
              data-testid="create-report-btn"
            >
              {loading ? 'Creating...' : 'Create Report'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Reports List Modal
const ReportsListModal = ({ isOpen, onClose, groupId }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchReports();
    }
  }, [isOpen]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/groups/${groupId}/dashboard/reports`);
      setReports(response.data.reports || []);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async (reportId, period) => {
    try {
      const response = await axios.get(
        `${API}/groups/${groupId}/dashboard/reports/${reportId}/pdf`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `group_dashboard_${period.replace(/\s/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleFinalize = async (reportId) => {
    try {
      await axios.put(`${API}/groups/${groupId}/dashboard/reports/${reportId}/finalize`);
      toast.success('Report finalized');
      fetchReports();
    } catch (error) {
      toast.error('Failed to finalize report');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-background-surface border border-border rounded-sm w-full max-w-3xl max-h-[80vh] overflow-hidden"
      >
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-heading font-semibold text-text-primary">Dashboard Reports</h2>
          <button onClick={onClose} className="text-text-tertiary hover:text-text-primary">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="text-center py-8 text-text-tertiary">Loading reports...</div>
          ) : reports.length === 0 ? (
            <div className="text-center py-8 text-text-tertiary">No reports created yet</div>
          ) : (
            <div className="space-y-3">
              {reports.map((report) => (
                <div
                  key={report.id}
                  className="p-4 bg-background rounded-sm border border-border hover:border-gold/30 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-text-primary font-medium">{report.reporting_period}</h3>
                      <p className="text-sm text-text-tertiary">
                        By {report.completed_by} • {new Date(report.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded-sm text-xs font-mono ${
                        report.status === 'finalized' 
                          ? 'bg-emerald-500/20 text-emerald-400' 
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {report.status}
                      </span>
                      {report.status === 'draft' && (
                        <button
                          onClick={() => handleFinalize(report.id)}
                          className="px-3 py-1 text-sm bg-gold/20 text-gold rounded-sm hover:bg-gold/30"
                        >
                          Finalize
                        </button>
                      )}
                      <button
                        onClick={() => handleDownloadPdf(report.id, report.reporting_period)}
                        className="p-2 bg-gold/20 text-gold rounded-sm hover:bg-gold/30"
                        data-testid={`download-report-${report.id}`}
                      >
                        <DownloadSimple size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

const GroupDashboard = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateReport, setShowCreateReport] = useState(false);
  const [showReportsList, setShowReportsList] = useState(false);

  useEffect(() => {
    if (groupId) {
      fetchDashboardData();
    }
  }, [groupId]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/groups/${groupId}/dashboard/data`);
      setDashboardData(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('You do not have access to this group');
        navigate('/dashboard');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <div className="flex-1 ml-64 flex items-center justify-center">
          <div className="text-gold font-mono">Loading group dashboard...</div>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <div className="flex-1 ml-64 flex items-center justify-center">
          <div className="text-center">
            <Warning size={48} className="text-red-400 mx-auto mb-4" />
            <h2 className="text-xl text-text-primary mb-2">Group Not Found</h2>
            <p className="text-text-tertiary mb-4">The requested group could not be loaded.</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 bg-gold text-gold-foreground rounded-sm"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { group, stats, division_scores, risk_appetites, top_risks } = dashboardData;

  return (
    <div className="flex min-h-screen bg-background" data-testid="group-dashboard">
      <Sidebar />
      <div className="flex-1 ml-64">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-background border-b border-border px-8 py-6">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-background-hover rounded-sm transition-colors"
            >
              <CaretLeft size={24} className="text-text-secondary" />
            </button>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gold/20 rounded-sm flex items-center justify-center">
                  <Buildings size={24} className="text-gold" />
                </div>
                <div>
                  <h1 className="text-2xl font-heading font-bold text-text-primary">
                    {group?.name || 'Group Dashboard'}
                  </h1>
                  <p className="text-sm text-text-tertiary">
                    {group?.description || 'Aggregate compliance posture across all divisions'}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowReportsList(true)}
                className="flex items-center gap-2 px-4 py-2 border border-border rounded-sm text-text-secondary hover:bg-background-hover transition-colors"
                data-testid="view-reports-btn"
              >
                <FileText size={20} />
                View Reports
              </button>
              <button
                onClick={() => setShowCreateReport(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gold text-gold-foreground rounded-sm font-medium hover:bg-gold/90 transition-colors"
                data-testid="create-report-btn"
              >
                <Plus size={20} />
                Create Report
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-8">
          {/* Stats Cards */}
          <div className="grid grid-cols-5 gap-4 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-background-surface border border-border rounded-sm p-6"
            >
              <div className="flex items-center gap-3 mb-3">
                <Buildings size={24} className="text-gold" />
                <span className="text-text-tertiary text-sm">Divisions</span>
              </div>
              <div className="text-3xl font-mono font-bold text-text-primary">
                {stats?.total_divisions || 0}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-background-surface border border-border rounded-sm p-6"
            >
              <div className="flex items-center gap-3 mb-3">
                <FileText size={24} className="text-blue-400" />
                <span className="text-text-tertiary text-sm">Total Legislation</span>
              </div>
              <div className="text-3xl font-mono font-bold text-text-primary">
                {stats?.total_legislation || 0}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-background-surface border border-border rounded-sm p-6"
            >
              <div className="flex items-center gap-3 mb-3">
                <Warning size={24} className="text-red-400" />
                <span className="text-text-tertiary text-sm">High Risk</span>
              </div>
              <div className="text-3xl font-mono font-bold text-red-400">
                {stats?.high_risk_count || 0}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-background-surface border border-border rounded-sm p-6"
            >
              <div className="flex items-center gap-3 mb-3">
                <Shield size={24} className="text-yellow-400" />
                <span className="text-text-tertiary text-sm">Medium Risk</span>
              </div>
              <div className="text-3xl font-mono font-bold text-yellow-400">
                {stats?.medium_risk_count || 0}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-background-surface border border-border rounded-sm p-6"
            >
              <div className="flex items-center gap-3 mb-3">
                <CheckCircle size={24} className="text-emerald-400" />
                <span className="text-text-tertiary text-sm">Low Risk</span>
              </div>
              <div className="text-3xl font-mono font-bold text-emerald-400">
                {stats?.low_risk_count || 0}
              </div>
            </motion.div>
          </div>

          <div className="grid grid-cols-3 gap-8 mb-8">
            {/* Overall Maturity Score */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-background-surface border border-border rounded-sm p-6"
            >
              <h2 className="text-lg font-heading font-semibold text-text-primary mb-6">
                Overall Maturity Score
              </h2>
              <div className="flex flex-col items-center">
                <MaturityRing score={stats?.overall_maturity_score || 0} />
                <div className="mt-4 text-center">
                  <span className={`text-sm font-medium ${getMaturityColor(stats?.overall_maturity_score || 0).text}`}>
                    {stats?.overall_maturity_score >= 3 ? 'Managed' : 
                     stats?.overall_maturity_score >= 2 ? 'Defined' : 
                     stats?.overall_maturity_score >= 1 ? 'Ad Hoc' : 'Not Implemented'}
                  </span>
                  <p className="text-xs text-text-tertiary mt-1">Group average across all divisions</p>
                </div>
              </div>
            </motion.div>

            {/* Division Compliance Scores */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="col-span-2 bg-background-surface border border-border rounded-sm p-6"
            >
              <h2 className="text-lg font-heading font-semibold text-text-primary mb-4">
                Division Compliance Scores
              </h2>
              {division_scores?.length > 0 ? (
                <div className="space-y-3">
                  {division_scores.map((div, idx) => {
                    const colors = getMaturityColor(div.maturity_score);
                    const percentage = (div.maturity_score / 4) * 100;
                    return (
                      <div key={div.division_id || idx} className="p-3 bg-background rounded-sm">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <span className="text-text-primary font-medium">{div.division_name}</span>
                            <span className="text-xs text-text-tertiary ml-2">({div.subdomain})</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-text-tertiary">
                              {div.themes_assessed} themes assessed
                            </span>
                            <span className={`px-2 py-1 rounded-sm text-xs font-mono ${colors.bg} ${colors.text} ${colors.border} border`}>
                              {div.maturity_score.toFixed(1)}/4
                            </span>
                          </div>
                        </div>
                        <div className="w-full h-2 bg-background-hover rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${percentage}%` }}
                            transition={{ duration: 1, delay: idx * 0.1 }}
                            className={`h-full ${colors.bg.replace('/20', '')}`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-text-tertiary">
                  <Info size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No division assessments completed yet</p>
                </div>
              )}
            </motion.div>
          </div>

          <div className="grid grid-cols-2 gap-8">
            {/* Risk Appetite by Theme */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="bg-background-surface border border-border rounded-sm p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-heading font-semibold text-text-primary">
                  Risk Appetite by Theme
                </h2>
                <Target size={24} className="text-gold" />
              </div>
              {risk_appetites?.length > 0 ? (
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {risk_appetites.map((ra, idx) => (
                    <div key={ra.theme || idx} className="p-3 bg-background rounded-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-text-primary font-medium">
                          {ra.theme_display_name}
                        </span>
                        <StatusBadge status={ra.status} />
                      </div>
                      <div className="flex items-center gap-4 text-xs">
                        <span className="text-text-tertiary">
                          Actual: <span className={getMaturityColor(ra.actual_score).text}>{ra.actual_score.toFixed(1)}</span>
                        </span>
                        <span className="text-text-tertiary">
                          Target: <span className="text-gold">{ra.desired_score.toFixed(1)}</span>
                        </span>
                        <span className={`${ra.gap > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                          Gap: {ra.gap > 0 ? '+' : ''}{ra.gap.toFixed(1)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-text-tertiary">
                  <Gauge size={32} className="mx-auto mb-2 opacity-50" />
                  <p>Complete assessments to view risk appetite</p>
                </div>
              )}
            </motion.div>

            {/* Top 5 Risks */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="bg-background-surface border border-border rounded-sm p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-heading font-semibold text-text-primary">
                  Top 5 Risks
                </h2>
                <Warning size={24} className="text-red-400" />
              </div>
              {top_risks?.length > 0 ? (
                <div className="space-y-3">
                  {top_risks.slice(0, 5).map((risk, idx) => (
                    <div key={risk.id || idx} className="p-3 bg-background rounded-sm border-l-2 border-red-500">
                      <div className="flex items-start justify-between mb-1">
                        <span className="text-sm text-text-primary font-medium line-clamp-1">
                          {idx + 1}. {risk.title}
                        </span>
                        <RiskBadge level={risk.risk_level} />
                      </div>
                      <div className="flex items-center gap-2 text-xs text-text-tertiary">
                        <span className="text-gold">{risk.division_name}</span>
                        <span>•</span>
                        <span>{risk.theme}</span>
                        {risk.regulation_reference && (
                          <>
                            <span>•</span>
                            <span>{risk.regulation_reference}</span>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-text-tertiary">
                  <Shield size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No risks identified yet</p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <CreateReportModal
        isOpen={showCreateReport}
        onClose={() => setShowCreateReport(false)}
        onSuccess={fetchDashboardData}
        groupId={groupId}
      />
      <ReportsListModal
        isOpen={showReportsList}
        onClose={() => setShowReportsList(false)}
        groupId={groupId}
      />
    </div>
  );
};

export default GroupDashboard;
